import Alert from '@mui/material/Alert';
import type { TFormQuestion } from '@onesource/services';
import type { TSubmitFormAnswer } from '@onesource/schemas';
import { MultipleChoiceQuestion } from './multiple-choice-question';
import { MultiSelectQuestion } from './multi-select-question';
import { TrueFalseQuestion } from './true-false-question';
import { ShortAnswerQuestion } from './short-answer-question';
import { OrderingQuestion } from './ordering-question';
import { FileUploadQuestion } from './file-upload-question';
import { AcceptQuestion } from './accept-question';
import { AcceptWithSignatureQuestion } from './accept-with-signature-question';
import { DropdownQuestion } from './dropdown-question';

interface QuestionRendererProps {
  question: TFormQuestion;
  answer: TSubmitFormAnswer;
  onAnswerChange: (answer: TSubmitFormAnswer) => void;
  disabled?: boolean;
}

/**
 * Routes to the appropriate question type component based on questionType
 */
export const QuestionRenderer = ({ question, answer, onAnswerChange, disabled = false }: QuestionRendererProps) => {
  const handleOptionSelect = (optionIds: string[]) => {
    onAnswerChange({
      ...answer,
      selectedOptionIds: optionIds,
    });
  };

  const handleTextChange = (text: string) => {
    onAnswerChange({
      ...answer,
      textAnswer: text,
    });
  };

  const handleOrderChange = (sequence: string[]) => {
    onAnswerChange({
      ...answer,
      orderingSequence: sequence,
    });
  };

  const handleFileUrlsChange = (fileUrls: string[]) => {
    onAnswerChange({
      ...answer,
      fileUrls,
    });
  };

  switch (question.questionType) {
    case 'multiple_choice': {
      return (
        <MultipleChoiceQuestion
          question={question}
          selectedOptionIds={answer.selectedOptionIds || []}
          onSelect={handleOptionSelect}
          disabled={disabled}
        />
      );
    }
    case 'multiple_select': {
      return (
        <MultiSelectQuestion
          question={question}
          selectedOptionIds={answer.selectedOptionIds || []}
          onSelect={handleOptionSelect}
          disabled={disabled}
        />
      );
    }
    case 'true_false': {
      return (
        <TrueFalseQuestion
          question={question}
          selectedOptionIds={answer.selectedOptionIds || []}
          onSelect={handleOptionSelect}
          disabled={disabled}
        />
      );
    }
    case 'short_answer': {
      return (
        <ShortAnswerQuestion
          question={question}
          textAnswer={answer.textAnswer || ''}
          onTextChange={handleTextChange}
          disabled={disabled}
        />
      );
    }
    case 'ordering': {
      return (
        <OrderingQuestion
          question={question}
          orderingSequence={answer.orderingSequence || []}
          onOrderChange={handleOrderChange}
          disabled={disabled}
        />
      );
    }
    case 'file_upload': {
      return (
        <FileUploadQuestion fileUrls={answer.fileUrls || []} onChange={handleFileUrlsChange} disabled={disabled} />
      );
    }
    case 'accept': {
      return <AcceptQuestion question={question} answer={answer} onAnswerChange={onAnswerChange} disabled={disabled} />;
    }
    case 'accept_with_signature': {
      return (
        <AcceptWithSignatureQuestion
          question={question}
          answer={answer}
          onAnswerChange={onAnswerChange}
          disabled={disabled}
        />
      );
    }
    case 'dropdown': {
      return (
        <DropdownQuestion question={question} answer={answer} onAnswerChange={onAnswerChange} disabled={disabled} />
      );
    }
    default: {
      return <Alert severity="warning">Unknown question type: {question.questionType}</Alert>;
    }
  }
};
